import { useCurrentAccountOrNull, type AccountRole } from './AccountContext';

/** What the signed-in person may do in the line they are currently in.
 *
 *  Named capabilities rather than role comparisons sprinkled through the pages: when
 *  `AccountRole` grows a level, or a policy in schema.zmodel moves an action between
 *  levels, there is one table to change instead of a search for `!== 'VIEWER'`.
 *
 *  This is NOT authorization — `@@allow` rules in schema.zmodel are, and they run on
 *  the server whatever the UI shows. This decides what to render, so a viewer meets a
 *  screen with no buttons rather than buttons that throw. `lib/policy.ts` cannot answer
 *  it: the account rules are collection predicates (`account.members?[…]`) and its
 *  evaluator returns undefined for those by design.
 *
 *  Each entry names the rule it mirrors, because the two have to be changed together. */
const CAPABILITIES = {
  /** inviteDelegate / setMemberRole / removeMember — all OWNER-only in their handlers. */
  manageTeam: ['OWNER'],
  /** AssistantConfig, FaqEntry, AgentKnowledgeDoc, OnboardingProgress, BlockedCaller and
   *  custom Tag writes: `@@allow('all', … role == 'OWNER' || role == 'ADMIN')`. */
  configureAssistant: ['OWNER', 'ADMIN'],
  /** Account update — renaming the line and changing retention are configuration. */
  configureAccount: ['OWNER', 'ADMIN'],
  /** AuditLog read: oversight, not day-to-day work, so not RESPONDER or VIEWER. */
  readAuditLog: ['OWNER', 'ADMIN'],
  /** Buying and releasing a number costs money → OWNER, through a procedure. */
  manageNumber: ['OWNER'],
  /** CallTag and Contact writes — working the calls, so anyone but VIEWER. */
  workCalls: ['OWNER', 'ADMIN', 'RESPONDER'],
  /** Billing is the line's own money. Nobody delegated to help answer calls should be
   *  reading the invoices. */
  viewBilling: ['OWNER'],
  /** `Contact.e164` and `User.email` carry field-level read rules in schema.zmodel that
   *  withhold them from a VIEWER: reading what the assistant took down is not the same
   *  as walking away with the account's phone book or its people's addresses. These two
   *  entries exist so the UI can render "withheld" instead of an empty gap, and must
   *  stay in step with those field rules — the server returns `null` either way. */
  seeContactNumbers: ['OWNER', 'ADMIN', 'RESPONDER'],
  seeTeamEmails: ['OWNER', 'ADMIN', 'RESPONDER'],
} satisfies Record<string, readonly AccountRole[]>;

export type Capability = keyof typeof CAPABILITIES;

export function roleCan(role: AccountRole, capability: Capability): boolean {
  return (CAPABILITIES[capability] as readonly AccountRole[]).includes(role);
}

/** `const can = useCan(); can('manageTeam')`.
 *
 *  Answers false outside a line rather than throwing: a screen that renders both inside
 *  and outside one should hide the action, not crash. */
export function useCan(): (capability: Capability) => boolean {
  const account = useCurrentAccountOrNull();
  return (capability) => (account ? roleCan(account.myRole, capability) : false);
}
